import { Box, Divider, FillPreview, FormField } from '@wix/design-system';
import React, { useContext } from 'react';
import { RicosContext } from 'ricos-context';
import type {
  PollData,
  PollData_Design_PollDesign_Background_Gradient,
  PollData_Design_PollDesign_Background_Type as PollBackgroundType,
} from 'ricos-schema/next';
import { PollData_Design_PollDesign_Background_Type } from 'ricos-schema/next';
import { SliderWithInput, ThumbnailPicker } from 'wix-rich-content-ui-components';

import { ColorIcon, GradientIcon, ImageIcon } from '../../assets/icons';
import {
  ANSWERS_CORNER_RADIUS_INPUT,
  ANSWERS_CORNER_RADIUS_SLIDER,
  POLL_BACKGROUND_COLOR,
  POLL_BACKGROUND_GRADIENT,
  POLL_BACKGROUND_IMAGE,
  POLL_CORDER_RADIUS_INPUT,
  POLL_CORDER_RADIUS_SLIDER,
} from '../../dataHooks';
import { BACKGROUND_PRESETS } from '../../defaults';
import { Section } from './section';

type DesignSettingsSectionProps = {
  componentData: PollData;
  updateRicosDocument: (data: PollData) => void;
};

export const DesignSettingsSection: React.FC<DesignSettingsSectionProps> = ({
  componentData,
  updateRicosDocument,
}) => {
  const { t } = useContext(RicosContext);

  const backgroundType =
    componentData.design?.poll?.background?.type ||
    PollData_Design_PollDesign_Background_Type.COLOR;

  const handlePollBorderRadiusChange = (borderRadius: number) => {
    updateRicosDocument({
      design: {
        ...componentData.design,
        poll: { ...componentData.design?.poll, borderRadius },
      },
    });
  };

  const handleOptionBorderRadiusChange = (borderRadius: number) => {
    updateRicosDocument({
      design: {
        ...componentData.design,
        options: { ...componentData.design?.options, borderRadius },
      },
    });
  };

  const handleBackgroundChange = (backgroundType: PollData_Design_PollDesign_Background_Type) => {
    const backgroundPreset = BACKGROUND_PRESETS[backgroundType][0];
    const bgValue =
      backgroundType === PollData_Design_PollDesign_Background_Type.GRADIENT
        ? { gradient: JSON.parse(backgroundPreset) }
        : backgroundType === PollData_Design_PollDesign_Background_Type.IMAGE
        ? { image: { src: { url: backgroundPreset } } }
        : { color: backgroundPreset };

    updateRicosDocument({
      design: {
        ...componentData.design,
        poll: {
          ...componentData.design?.poll,
          background: { ...bgValue, type: backgroundType as PollBackgroundType },
        },
      },
    });
  };

  const handleBackgroundColorChange = value => {
    const bgValue =
      backgroundType === PollData_Design_PollDesign_Background_Type.GRADIENT
        ? { gradient: JSON.parse(value) }
        : backgroundType === PollData_Design_PollDesign_Background_Type.IMAGE
        ? { image: { src: { url: value } } }
        : { color: value };

    updateRicosDocument({
      design: {
        ...componentData.design,
        poll: {
          ...componentData.design?.poll,
          background: { ...bgValue, type: backgroundType as PollBackgroundType },
        },
      },
    });
  };

  return (
    <>
      <Section title={t('Poll_PollSettings_Tab_Design_Section_Background_Header')}>
        <ThumbnailPicker
          dataSource={[
            {
              dataHook: POLL_BACKGROUND_COLOR,
              value: PollData_Design_PollDesign_Background_Type.COLOR,
              label: t('Poll_PollSettings_Tab_Design_Section_Background_Color'),
              renderBackgroundImage: props => <ColorIcon {...props} />,
            },
            {
              dataHook: POLL_BACKGROUND_GRADIENT,
              value: PollData_Design_PollDesign_Background_Type.GRADIENT,
              label: t('Poll_PollSettings_Tab_Design_Section_Background_Gradient'),
              renderBackgroundImage: props => <GradientIcon {...props} />,
            },
            {
              dataHook: POLL_BACKGROUND_IMAGE,
              value: PollData_Design_PollDesign_Background_Type.IMAGE,
              label: t('Poll_PollSettings_Tab_Design_Section_Background_Pattern'),
              renderBackgroundImage: props => <ImageIcon {...props} />,
            },
          ]}
          selectedValue={backgroundType}
          title={t('Poll_PollSettings_Tab_Design_Section_Background_Choose')}
          onChange={handleBackgroundChange}
        />
      </Section>
      <Box direction="vertical" marginTop="SP3" marginBottom="SP2">
        <FormField label={t(PICK_BACKGROUND_LABELS[backgroundType])}>
          <Box marginTop="SP2" marginBottom="SP2" gap="SP2" verticalAlign="middle">
            {BACKGROUND_PRESETS[backgroundType].map((color, index) => {
              const background = componentData.design?.poll?.background;
              return (
                <Box width="36px" key={index}>
                  <FillPreview
                    selected={
                      !background
                        ? false
                        : background.type === 'COLOR'
                        ? color === background?.color
                        : background.type === 'IMAGE'
                        ? color === background?.image?.src?.url
                        : background.type === 'GRADIENT'
                        ? color === JSON.stringify(background.gradient)
                        : false
                    }
                    onClick={() => {
                      handleBackgroundColorChange(color);
                    }}
                    fill={
                      componentData.design?.poll?.background?.type === 'GRADIENT'
                        ? gradientColorObjToString(JSON.parse(color))
                        : color
                    }
                  />
                </Box>
              );
            })}
          </Box>
        </FormField>
      </Box>
      <Divider />
      <Section title={t('Poll_PollSettings_Tab_Design_Section_CornerRadius_Header')}>
        <Box marginTop="SP2" marginBottom="SP2">
          <SliderWithInput
            min={0}
            max={20}
            label={t('Poll_PollSettings_Tab_Design_Section_CornerRadius_Poll')}
            onChange={handlePollBorderRadiusChange}
            value={componentData.design?.poll?.borderRadius ?? 0}
            sliderDataHook={POLL_CORDER_RADIUS_SLIDER}
            inputDataHook={POLL_CORDER_RADIUS_INPUT}
          />
        </Box>
        <Box marginTop="SP2" marginBottom="SP2">
          <SliderWithInput
            min={0}
            max={10}
            label={t('Poll_PollSettings_Tab_Design_Section_CornerRadius_Answers')}
            onChange={handleOptionBorderRadiusChange}
            value={componentData.design?.options?.borderRadius ?? 0}
            sliderDataHook={ANSWERS_CORNER_RADIUS_SLIDER}
            inputDataHook={ANSWERS_CORNER_RADIUS_INPUT}
          />
        </Box>
      </Section>
    </>
  );
};

const PICK_BACKGROUND_LABELS: Record<PollData_Design_PollDesign_Background_Type, string> = {
  [PollData_Design_PollDesign_Background_Type.COLOR]:
    'Poll_PollSettings_Tab_Design_Section_Background_Color_Pick',
  [PollData_Design_PollDesign_Background_Type.IMAGE]:
    'Poll_PollSettings_Tab_Design_Section_Background_Pattern_Pick',
  [PollData_Design_PollDesign_Background_Type.GRADIENT]:
    'Poll_PollSettings_Tab_Design_Section_Background_Gradient_Pick',
};

const gradientColorObjToString = (gradient: PollData_Design_PollDesign_Background_Gradient) => {
  return `linear-gradient(${gradient?.angle}deg, ${gradient.startColor} 0%, ${gradient.lastColor} 100%)`;
};
